/* ============================================================
   PP COLLECTION — filter / sort behaviour for sections/pp-collection.liquid.
   Also drives the SEARCH results page, which renders through the same section.
   ============================================================

   WHY THIS IS A CUSTOM ELEMENT AND NOT THE INLINE <script> IT REPLACES
   Applying a filter no longer reloads the page: it re-renders THIS section through Shopify's
   Section Rendering API and morphs the result into place (assets/section-renderer.js, which
   calls assets/morph.js). morph does not re-execute inline scripts. An inline script would
   therefore have worked exactly once — after the first tick the drawer would stop opening,
   ESC would stop closing it and the focus trap would be gone. A custom element survives a
   morph: the browser keeps the element and every listener bound to it, and because all
   listeners here are delegated from the element, controls rebuilt by the re-render are covered
   without re-binding anything.

   WHY sectionRenderer AND NOT Horizon's facets-form-component (assets/facets.js)
   facets.js is the layer ABOVE this one: it reads Horizon's own facet markup (ref="facetsForm",
   blocks/filters.liquid) and then calls exactly the sectionRenderer.renderSection() used below.
   Adopting it would mean adopting that markup — a file outside this section — and taking a hard
   import on the external @shopify/events bundle, whose failure would take filtering down with it.
   So we reuse the machinery it reuses (Section Rendering API + morph), not a copy of it.

   WHY THE STATE CLASSES LIVE ON <html>
   morph rewrites the section's own markup from the server response. Any class JavaScript had
   added to an element INSIDE the section — "filters are enhanced", "drawer is open" — is wiped
   the moment a filter is applied, which on mobile would slam the drawer shut mid-tick and drop
   the bottom-docked toolbar back into the page flow. <html> is outside the morphed subtree, so
   state parked there is the one thing a re-render cannot touch. Attributes that must stay on
   elements inside the section (aria-expanded, role=dialog) are re-applied after every render.
*/

import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { lockScroll, unlockScroll } from '@theme/utilities';

/* Set on <html>, never inside the section — see the header note. */
const CLASS_JS = 'pp-col-js';
const CLASS_AUTOFILTER = 'pp-col-autofilter';
const CLASS_SHEET_OPEN = 'pp-col-sheet-open';

/* Must match the 60em breakpoint in pp-collection.css: below it the filters are a slide-up
   drawer, above it they are an open row of dropdowns. */
const WIDE = '(min-width: 60em)';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Whether a click asked the browser for something other than a plain navigation
 * (new tab, new window, download). Those must be left alone or we would swallow them.
 * @param {MouseEvent} event
 * @returns {boolean}
 */
function isModifiedClick(event) {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

class PpCollection extends Component {
  /** @type {MediaQueryList} */
  #mqWide = window.matchMedia(WIDE);

  /** @type {Element | null} */
  #lastFocus = null;

  /** True only when this element is the one that locked page scrolling. */
  #lockedScroll = false;

  /**
   * Path this section belongs to. popstate also fires for history entries other components
   * pushed; re-rendering on those would fetch a URL this section does not describe.
   * @type {string}
   */
  #basePath = '';

  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  /** The filter + sort form. Re-queried every time: a re-render may hand us a new node. */
  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-pp-tools-form]'));
  }

  get #panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pp-sheet]'));
  }

  get #openBtn() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pp-sheet-open]'));
  }

  connectedCallback() {
    super.connectedCallback();

    const html = document.documentElement;
    html.classList.add(CLASS_JS);

    const form = this.#form;
    if (form) {
      /* Only now is the "Filter" submit button redundant, so only now is it hidden — and only on
         desktop. In the mobile drawer it stays: it is what closes the drawer and takes the
         shopper back to the products they just filtered. */
      html.classList.add(CLASS_AUTOFILTER);
      try {
        this.#basePath = new URL(form.action, window.location.href).pathname;
      } catch {
        this.#basePath = window.location.pathname;
      }
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
    /* `toggle` does not bubble, so it can only be caught on the way DOWN. */
    this.addEventListener('toggle', this.#onToggle, true);

    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
    window.addEventListener('popstate', this.#onPopState);
    this.#mqWide.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('popstate', this.#onPopState);
    this.#mqWide.removeEventListener('change', this.#onBreakpoint);
    this.#unlockScroll();
    document.documentElement.classList.remove(CLASS_SHEET_OPEN);
  }

  /**
   * Fired by morph after a re-render touched this subtree. The drawer's ARIA attributes live on
   * elements the server just rewrote, so they have to be put back or a screen reader would be
   * told the drawer is closed while it is on screen.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncSheet();
  }

  /* ---------- applying filters ---------- */

  /**
   * Builds the URL the current form state describes.
   * Built from the form's own action, NOT from the current location: that is what drops `page`,
   * so filtering from page 3 lands on page 1 of the new result set instead of an empty page 3.
   * @returns {URL | null}
   */
  #urlFromForm() {
    const form = this.#form;
    if (!form) return null;

    const url = new URL(form.action, window.location.href);
    url.search = '';

    /* Empty values are dropped one by one rather than with URLSearchParams.delete(name), which
       would remove every value sharing that name — and a size filter is several values under one
       name. The empty ones are the price fields the shopper left blank. */
    for (const [key, value] of new FormData(form)) {
      if (typeof value !== 'string' || value === '') continue;
      url.searchParams.append(key, value);
    }

    return url;
  }

  /**
   * @param {string} [href] - a ready-made URL (a filter chip, "clear all"); omit to read the form
   */
  #apply(href) {
    let url = null;
    try {
      url = href ? new URL(href, window.location.href) : this.#urlFromForm();
    } catch {
      url = null;
    }
    if (!url) return;

    /* pushState, not replaceState: ticking a filter is a step the shopper expects Back to undo. */
    history.pushState({ ppCollection: true }, '', url.toString());
    this.#render(url);
  }

  /**
   * @param {URL} url
   */
  async #render(url) {
    const state = this.#capture();
    this.setAttribute('aria-busy', 'true');

    try {
      /* new URL(url): buildSectionRenderingURL mutates what it is handed. */
      await sectionRenderer.renderSection(this.sectionId, { url: new URL(url.toString()) });
    } catch {
      /* Network died or the section could not be found. Leaving stale products under a URL that
         says they are filtered is worse than a reload, so fall back to a real navigation. */
      window.location.assign(url.toString());
      return;
    }

    this.removeAttribute('aria-busy');
    this.#restore(state);
    this.#syncSheet();
  }

  /**
   * Reads the bits of UI state the server response knows nothing about. Without this, ticking one
   * size on desktop closes the dropdown it was ticked in, so a second size cannot be added
   * without re-opening it.
   */
  #capture() {
    const active = document.activeElement;
    let focusName = '';
    let focusValue = '';

    if (active instanceof HTMLElement && this.contains(active) && 'name' in active) {
      focusName = /** @type {HTMLInputElement} */ (active).name || '';
      focusValue = /** @type {HTMLInputElement} */ (active).value || '';
    }

    return {
      open: Array.from(this.querySelectorAll('[data-pp-f]')).map((d) => /** @type {HTMLDetailsElement} */ (d).open),
      panelScroll: this.#panel ? this.#panel.scrollTop : 0,
      focusName,
      focusValue,
    };
  }

  /**
   * @param {{ open: boolean[], panelScroll: number, focusName: string, focusValue: string }} state
   */
  #restore(state) {
    const groups = this.querySelectorAll('[data-pp-f]');
    groups.forEach((group, index) => {
      if (state.open[index]) group.setAttribute('open', '');
    });

    const panel = this.#panel;
    if (panel && state.panelScroll) panel.scrollTop = state.panelScroll;

    /* Only step in when the re-render actually dropped focus — morph usually keeps the node, and
       stealing focus back from wherever the shopper moved it would be worse than doing nothing. */
    if (!state.focusName) return;
    const active = document.activeElement;
    if (active && active !== document.body && this.contains(active)) return;

    const name = `[name="${CSS.escape(state.focusName)}"]`;
    const target =
      (state.focusValue && this.querySelector(`${name}[value="${CSS.escape(state.focusValue)}"]`)) ||
      this.querySelector(name);

    /* preventScroll: refocusing must not undo the scroll position this whole feature exists to keep. */
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /* ---------- events ---------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement) || !el.closest('[data-pp-tools-form]')) return;

    /* Changing the sort is a clear intent, so apply right away and save a click. */
    if (el.matches('[data-pp-sort]')) {
      this.#apply();
      return;
    }

    /* Ticking a filter applies straight away. This used to wait for the "Filter" button on the
       grounds that shoppers tick several sizes at once — which reads sensibly and is wrong in
       practice: nothing on screen says the tick has not taken effect, so the page just looks
       broken until the button is found.
       Guarded to checkboxes: the price fields fire `change` on blur, which would apply the moment
       focus left a half-typed number. Those keep Enter and the button. */
    if (el instanceof HTMLInputElement && el.type === 'checkbox') this.#apply();
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    /* The in-page search box shows the query without its wildcard, so put one back on submit —
       otherwise refining a search from the results page runs a whole-word match and returns FEWER
       results than the page the shopper is looking at, which reads as the site losing products.
       Same rule as the header box (assets/predictive-search.js): last word only, and never over a
       term the shopper gave their own operator or quotes to. Rewriting the field rather than
       hijacking navigation keeps this a plain GET form, so with JS off it still searches. */
    if (form.matches('[data-pp-q-form]')) {
      const input = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-pp-q-input]'));
      if (!input) return;
      const terms = input.value.trim().split(/\s+/);
      const last = terms[terms.length - 1];
      if (last && !/[*"]/.test(last)) {
        terms[terms.length - 1] = `${last}*`;
        input.value = terms.join(' ');
      }
      return;
    }

    if (!form.matches('[data-pp-tools-form]')) return;

    event.preventDefault();
    this.#apply();
    /* In the drawer the same button is the way out: it is what takes the shopper back to the
       products they just filtered. On desktop there is nothing to close. */
    if (!this.#mqWide.matches) this.#closeSheet(true);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-pp-sheet-open]')) {
      this.#openSheet();
      return;
    }

    if (target.closest('[data-pp-sheet-close]')) {
      this.#closeSheet(true);
      return;
    }

    /* Removing one filter and clearing them all are links carrying a server-built URL, so they
       need no form reading — but they must go through the same in-place render as a tick, or the
       shopper would lose their place the moment they undid one thing. */
    const link = target.closest('a.pp-col__chip, a.pp-col__clear');
    if (link instanceof HTMLAnchorElement && !isModifiedClick(event)) {
      event.preventDefault();
      this.#apply(link.href);
    }
  };

  /**
   * On desktop only one filter group should be open at a time; overlapping ones hide each other.
   * @param {Event} event
   */
  #onToggle = (event) => {
    const group = event.target;
    if (!(group instanceof HTMLDetailsElement) || !group.matches('[data-pp-f]')) return;
    if (!group.open || !this.#mqWide.matches) return;

    for (const other of this.querySelectorAll('[data-pp-f][open]')) {
      if (other !== group) other.removeAttribute('open');
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Tab') {
      this.#trapTab(event);
      return;
    }

    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    if (document.documentElement.classList.contains(CLASS_SHEET_OPEN)) {
      event.preventDefault();
      this.#closeSheet(true);
      return;
    }

    /* ESC also closes an open filter group on desktop, and returns focus to that group's summary */
    const open = this.querySelector('[data-pp-f][open]');
    if (open && open.contains(document.activeElement)) {
      open.removeAttribute('open');
      const summary = open.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus();
    }
  };

  /**
   * Trap focus inside the drawer while it is open, otherwise Tab escapes behind the scrim.
   * @param {KeyboardEvent} event
   */
  #trapTab(event) {
    if (!document.documentElement.classList.contains(CLASS_SHEET_OPEN)) return;
    const items = this.#focusables();
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#mqWide.matches) return;
    for (const group of this.querySelectorAll('[data-pp-f][open]')) {
      if (!group.contains(/** @type {Node} */ (event.target))) group.removeAttribute('open');
    }
  };

  #onPopState = () => {
    /* Other components push history entries too. Re-rendering on one of those would ask the
       Section Rendering API for a URL this section does not describe. */
    if (this.#basePath && window.location.pathname !== this.#basePath) return;
    this.#render(new URL(window.location.href));
  };

  #onBreakpoint = () => {
    /* Rotating to a wide viewport makes the sliding drawer meaningless */
    if (this.#mqWide.matches) this.#closeSheet(false);
  };

  /* ---------- the mobile drawer ---------- */

  /** @returns {HTMLElement[]} */
  #focusables() {
    const panel = this.#panel;
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null
    );
  }

  #openSheet() {
    const panel = this.#panel;
    if (!panel) return;

    this.#lastFocus = document.activeElement;
    document.documentElement.classList.add(CLASS_SHEET_OPEN);
    /* The theme's own lock, not body{overflow:hidden}. Horizon makes .page-wrapper the scroll
       container above 990px, so freezing <body> freezes nothing — and the lock is reference
       counted, so opening this drawer cannot release a lock the cart drawer still holds. */
    lockScroll(this);
    this.#lockedScroll = true;
    this.#syncSheet();

    const items = this.#focusables();
    if (items.length) items[0].focus();
  }

  /** @param {boolean} returnFocus */
  #closeSheet(returnFocus) {
    const html = document.documentElement;
    if (!html.classList.contains(CLASS_SHEET_OPEN)) return;

    html.classList.remove(CLASS_SHEET_OPEN);
    this.#unlockScroll();
    this.#syncSheet();

    /* Return focus to the button that opened it, otherwise keyboard users get thrown back to the
       top of the page. The node may have been rebuilt by a re-render, so fall back to the button
       that is on screen now rather than to a detached one. */
    if (returnFocus === false) return;
    const back = this.#lastFocus instanceof HTMLElement && this.contains(this.#lastFocus) ? this.#lastFocus : this.#openBtn;
    if (back instanceof HTMLElement) back.focus();
  }

  #unlockScroll() {
    if (!this.#lockedScroll) return;
    unlockScroll(this);
    this.#lockedScroll = false;
  }

  /** Re-applies the drawer's ARIA state to elements a re-render may have rewritten. */
  #syncSheet() {
    const open = document.documentElement.classList.contains(CLASS_SHEET_OPEN);
    const panel = this.#panel;
    const btn = this.#openBtn;

    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!panel) return;

    if (!open) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-labelledby');
      return;
    }

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    const title = panel.querySelector('.pp-col__panel-title');
    if (title && title.id) panel.setAttribute('aria-labelledby', title.id);
  }
}

if (!customElements.get('pp-collection')) {
  customElements.define('pp-collection', PpCollection);
}
